import { Plural, Trans } from "@lingui/react/macro"
import { Button, SimpleGrid } from "@mantine/core"
import { IconArrowUpRight, IconSparkles } from "@tabler/icons-react"
import { useState } from "react"
import type { IAskCitation } from "../../lib/types/ask"
import { EvidenceCard } from "../EvidenceCard"
import { Markdown } from "../Markdown"
import { StaggerReveal } from "../StaggerReveal"
import { distinctMeetingCount, splitVerdict } from "./helpers"
import type { IProps } from "./interfaces"

import classes from "./styles.module.css"

/** How many evidence cards show before the "more sources" expander. */
const EVIDENCE_CAP = 6

/** The grounding sources under an answer — a responsive grid capped at
 * {@link EVIDENCE_CAP}, with a "+N more sources" expander, each card rising in on
 * a stagger (reduced-motion safe via {@link StaggerReveal}). */
function EvidenceGrid({ citations }: { citations: IAskCitation[] }): React.JSX.Element {
  const [expanded, setExpanded] = useState(false)
  const visible = expanded ? citations : citations.slice(0, EVIDENCE_CAP)
  const hidden = citations.length - visible.length

  return (
    <div className={classes.evidence}>
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="xs" verticalSpacing="xs">
        <StaggerReveal active stepMs={55}>
          {visible.map((citation, index) => (
            // Labels (C1, C2…) are unique per answer; index guards a repeat.
            <EvidenceCard key={`${citation.label}-${index}`} citation={citation} />
          ))}
        </StaggerReveal>
      </SimpleGrid>
      {hidden > 0 ? (
        <Button
          variant="subtle"
          color="brand"
          size="xs"
          className={classes.moreSources}
          onClick={() => setExpanded(true)}
        >
          <Plural value={hidden} one="+# more source" other="+# more sources" />
        </Button>
      ) : null}
    </div>
  )
}

/** Suggested follow-up questions as tappable pills; each submits itself in the
 * current session. */
function FollowUps({
  followUps,
  onFollowUp,
}: {
  followUps: string[]
  onFollowUp: (question: string) => void
}): React.JSX.Element {
  return (
    <div className={classes.followUps}>
      {followUps.map((question) => (
        <button type="button" key={question} className={classes.followUp} onClick={() => onFollowUp(question)}>
          <span className={classes.followUpText}>{question}</span>
          <IconArrowUpRight size={13} className={classes.followUpIcon} aria-hidden />
        </button>
      ))}
    </div>
  )
}

/**
 * One turn in the Ask-AI chat thread. Pure presentation.
 *
 * A `user` message is a right-aligned brand bubble. An assistant message renders
 * (by phase): a "thinking" typing indicator, a "warming" preparing state, or —
 * when `complete` — the grounded answer in the evidence-first layout: the first
 * paragraph becomes a tinted "Short answer" verdict block (background tint + a
 * small eyebrow, deliberately no left-border accent), the rest renders as
 * markdown, then an evidence grid of {@link EvidenceCard}s, optional follow-up
 * chips, and an "Answered from N meeting(s)" footnote (the model id is no longer
 * surfaced). A `refused` answer keeps a distinct muted, plain treatment with no
 * verdict / evidence. Inline `[C1]` refs are not markdown links, so they survive
 * as literal text. The per-role look rides `data-role` / `data-refused`
 * attributes, never a runtime colour branch (theming rule #5).
 */
export const ChatMessage = ({
  role,
  content,
  citations,
  refused = false,
  phase = "complete",
  followUps,
  onFollowUp,
}: IProps): React.JSX.Element => {
  const isAssistant = role === "assistant"
  const isOpenAnswer = isAssistant && !refused && phase === "complete"

  // The evidence-first open answer — no bordered bubble; the verdict tint, the
  // evidence cards and the follow-up pills carry their own surfaces.
  if (isOpenAnswer) {
    const { verdict, body } = splitVerdict(content)
    const meetingCount = distinctMeetingCount(citations)
    const showFollowUps = !!onFollowUp && !!followUps && followUps.length > 0

    return (
      <div className={classes.row} data-role={role}>
        <span className={classes.avatar} aria-hidden>
          <IconSparkles size={15} />
        </span>
        <div className={classes.answerBlock}>
          {verdict ? (
            <div className={classes.verdict}>
              <span className={classes.verdictEyebrow}>
                <Trans>Short answer</Trans>
              </span>
              <Markdown content={verdict} className={classes.verdictBody} />
            </div>
          ) : null}
          {body ? <Markdown content={body} className={classes.answer} /> : null}
          {citations && citations.length > 0 ? <EvidenceGrid citations={citations} /> : null}
          {showFollowUps ? <FollowUps followUps={followUps} onFollowUp={onFollowUp} /> : null}
          {meetingCount > 0 ? (
            <p className={classes.footnote}>
              <Plural value={meetingCount} one="Answered from # meeting" other="Answered from # meetings" />
            </p>
          ) : null}
        </div>
      </div>
    )
  }

  // In-flight placeholders + plain bubbles (user question / refusal).
  const bubbleBody = ((): React.JSX.Element => {
    if (isAssistant && phase === "thinking") {
      return (
        <div className={classes.pending} aria-live="polite">
          <span className={classes.dots} aria-hidden>
            <span className={classes.dot} />
            <span className={classes.dot} />
            <span className={classes.dot} />
          </span>
          <span className={classes.pendingLabel}>
            <Trans>Thinking…</Trans>
          </span>
        </div>
      )
    }
    if (isAssistant && phase === "warming") {
      return (
        <div className={classes.pending} aria-live="polite">
          <span className={classes.spinner} aria-hidden />
          <span className={classes.pendingLabel}>
            <Trans>Preparing your meetings…</Trans>
          </span>
        </div>
      )
    }
    return <p className={classes.text}>{content}</p>
  })()

  return (
    <div className={classes.row} data-role={role}>
      {isAssistant ? (
        <span className={classes.avatar} aria-hidden>
          <IconSparkles size={15} />
        </span>
      ) : null}
      <div className={classes.bubble} data-role={role} data-refused={refused ? "true" : undefined}>
        {bubbleBody}
      </div>
    </div>
  )
}
